import { useCurrentPageAccess } from "@/app/router";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { toast } from "@/shared/hooks/useToast";
import { RotateCw, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { ChannelDelayThresholdTable } from "../components/ChannelDelayThresholdTable";
import { validateHoursMinutes } from "../hooks/useChannelDelayThresholds";
import type { ChannelDelayThresholdRow } from "../types/preferences_types";
import {
  useChannelDelayThresholdsQuery,
  useSaveChannelDelayThresholdsMutation,
} from "../api/channelDelayThresholds";

export function PreferencesChannelCollectionDelayPage() {
  // React Query 훅들
  const { data: initialRows = [], isLoading, refetch } = useChannelDelayThresholdsQuery();
  const saveMutation = useSaveChannelDelayThresholdsMutation();

  // 로컬 상태로 rows 관리 (편집용)
  const [rows, setRows] = useState<ChannelDelayThresholdRow[]>([]);

  // initialRows가 변경되면 로컬 상태 업데이트 (새로고침 또는 저장 후)
  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(initialRows);
    }
  }, [initialRows]);

  const updateRow = (index: number, patch: Partial<Pick<ChannelDelayThresholdRow, "hours" | "minutes">>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleSave = async () => {
    // validate all
    for (const r of rows) {
      const v = validateHoursMinutes(String(r.hours), String(r.minutes));
      if (!v.ok) {
        toast({ title: v.error, variant: "destructive" });
        return;
      }
    }

    // rows를 직접 전송 (백엔드에서 초 단위로 변환)
    await saveMutation.mutateAsync(rows);
  };

  const { canWriteCurrentPage } = useCurrentPageAccess();

  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader
        title="채널별 수집 지연 시간 정의"
        description="각 매체별로 수집 작업이 지연되었다고 판단하는 기준 시간을 설정합니다. 설정된 시간을 초과하면 슬랙 알림이 발송됩니다."
        actions={
          <>
            <Button variant="outline" onClick={() => refetch()} disabled={isLoading || saveMutation.isPending}>
              <RotateCw className="mr-2 h-4 w-4" />
              새로고침
            </Button>
            <Button onClick={handleSave} disabled={isLoading || saveMutation.isPending || !canWriteCurrentPage}>
              <Save className="mr-2 h-4 w-4" />
              저장
            </Button>
          </>
        }
      />

      <Card className="p-4 text-sm text-muted-foreground">
        기본값은 3시간입니다. 파라미터에 저장되지 않은 채널은 기본값으로 표시되며, 저장 시 함께 저장됩니다.
      </Card>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      ) : (
        <ChannelDelayThresholdTable rows={rows} onChange={updateRow} />
      )}
    </div>
  );
}
